import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ChefHat,
  Eye,
  EyeOff,
  Loader2,
  QrCode,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import {
  ApiRequestError,
  api,
  type Me,
  type PlatformAdmin,
} from "../lib/api";

type LoginResponse =
  | ({ realm: "owner" } & Me)
  | { realm: "platform"; admin: PlatformAdmin; redirectTo?: string };

export function LoginPage({ onLogin }: { onLogin: (me: Me) => void }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await api<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (response.realm === "platform") {
        navigate("/platform", { replace: true });
        return;
      }

      onLogin({
        user: response.user,
        tenant: response.tenant,
        membership: response.membership,
      });
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat terhubung ke server. Coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <aside className="brand-panel">
        <div className="brand-panel__top">
          <Link to="/" className="brand-lockup brand-lockup--link">
            <BrandMark />
            <div>
              <strong>Cafe System Control</strong>
              <span>Platform &amp; Workspace</span>
            </div>
          </Link>
          <span className="brand-pill">
            <Sparkles size={13} />
            Platform pesanan F&B
          </span>
        </div>

        <div className="brand-panel__body">
          <p className="eyebrow eyebrow--light">
            Satu alur, dari meja ke dapur
          </p>
          <h1>
            Meja terisi.
            <br />
            Pesanan rapi.
            <br />
            <em>Owner tenang.</em>
          </h1>
          <p className="brand-lead">
            QR di meja, pembayaran QRIS, antrean dapur, dan laporan owner dalam
            satu alur yang tenang dan terukur.
          </p>

          <div className="brand-features">
            <div className="brand-feature">
              <QrCode size={17} />
              <span>QR per meja</span>
            </div>
            <div className="brand-feature">
              <Wallet size={17} />
              <span>QRIS otomatis</span>
            </div>
            <div className="brand-feature">
              <ChefHat size={17} />
              <span>Dapur real-time</span>
            </div>
          </div>
        </div>

        <div className="brand-panel__footer">
          <ShieldCheck size={16} />
          <span>Dirancang untuk cafe, kost, dan operasional multi-outlet.</span>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <Link
            to="/"
            className="auth-card__brand auth-card__brand--link"
          >
            <BrandMark size="sm" />
            <span>Cafe System Control</span>
          </Link>

          <Link to="/" className="auth-back">
            <ArrowLeft size={14} /> Kembali ke beranda
          </Link>

          <p className="eyebrow">Satu pintu masuk</p>
          <h2>Masuk ke akun Anda</h2>
          <p className="auth-subtitle">
            Owner dan superadmin memakai halaman ini. Sistem mengenali akun
            Anda dan mengarahkan ke workspace yang sesuai.
          </p>

          {error ? (
            <div className="alert alert--danger" role="alert">
              <AlertCircle size={17} />
              <span>{error}</span>
            </div>
          ) : null}

          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="field">
              <span>Username</span>
              <input
                className="input"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Masukkan username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                minLength={3}
                maxLength={32}
                required
              />
            </label>

            <label className="field">
              <span>Password</span>
              <span className="input-group">
                <input
                  className="input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Masukkan password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="input-group__action"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={
                    showPassword ? "Sembunyikan password" : "Tampilkan password"
                  }
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </span>
            </label>

            <div className="auth-links">
              <span className="auth-footnote auth-footnote--inline">
                Masuk memakai username, bukan email.
              </span>
              <Link className="auth-link" to="/forgot-password">
                Lupa password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="spin" size={17} />
                  Memeriksa akses...
                </>
              ) : (
                <>
                  Masuk
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </form>

          <p className="auth-footnote">
            Belum punya akun owner?{" "}
            <Link className="auth-link" to="/register">
              Daftar usaha Anda
            </Link>
            . Akun superadmin dibuat dari platform, bukan lewat pendaftaran.
          </p>
        </div>
      </main>
    </div>
  );
}
